import { FORM_SUBMIT_STATUS } from './constants/form-constants';
import { FieldSubscriber, FormSubmitStatus, FormfieldValues } from './types/form-types';

class FormService {
	private formSubmitStatusValue: FormSubmitStatus = FORM_SUBMIT_STATUS.PENDING;
	private fieldValues: FormfieldValues = {};
	private fieldValueSubscribers: Map<string, Set<FieldSubscriber>> = new Map();
	private fieldErrors = new Set<string>();
	private fieldErrorSubscribers: Map<string, Set<(hasError: boolean) => void>> = new Map();
	private requiredFields = new Set<string>();
	private suggestionFields = new Map<string, string>();
	private validationSubscribers = new Set<() => void>();
	private formStatusSubscribers = new Set<(status: FormSubmitStatus) => void>();

	//////////////////////////
	//Gestion del formulario//
	//////////////////////////
	formSubmitStatus(): FormSubmitStatus {
		return this.formSubmitStatusValue;
	}

	setFormSubmitStatus(status: FormSubmitStatus): void {
		this.formSubmitStatusValue = status;
		this.formStatusSubscribers.forEach((fn) => fn(status));
	}

	subscribeToFormSubmitStatus(callback: (status: FormSubmitStatus) => void): () => void {
		this.formStatusSubscribers.add(callback);
		return () => {
			this.formStatusSubscribers.delete(callback);
		};
	}

	/////////////////////
	//Gestion de campos//
	/////////////////////
	// Obtenemos el valor del campo
	getFieldValue<T = any>(name: string): T | undefined {
		return this.fieldValues[name];
	}

	// Obtenemos todos los valores de los campos del formulario
	getFieldsValues<T = FormfieldValues>(): T {
		return this.fieldValues as T;
	}

	// Añadimos el valor del campo al service
	setFieldValue<T = any>(name: string, value: T): void {
		if (this.fieldValues[name] === value) return;

		this.fieldValues[name] = value;
		// Notificamos al suscriptor del campo para que modifique el estado local también
		this.notifyField(name, value);
	}

	// Función para suscribir un campo
	subscribeToField(
		fieldName: string,
		callbacks: {
			onChange?: (value: any) => void;
			onError?: (hasError: boolean) => void;
		},
	): () => void {
		const unsubscribers: (() => void)[] = [];

		if (callbacks.onChange) {
			if (!this.fieldValueSubscribers.has(fieldName)) {
				this.fieldValueSubscribers.set(fieldName, new Set());
			}
			this.fieldValueSubscribers.get(fieldName)!.add(callbacks.onChange);
			unsubscribers.push(() => {
				const subscribers = this.fieldValueSubscribers.get(fieldName);
				if (subscribers) {
					subscribers.delete(callbacks.onChange!);
					if (subscribers.size === 0) {
						this.fieldValueSubscribers.delete(fieldName);
					}
				}
			});
		}

		if (callbacks.onError) {
			if (!this.fieldErrorSubscribers.has(fieldName)) {
				this.fieldErrorSubscribers.set(fieldName, new Set());
			}
			this.fieldErrorSubscribers.get(fieldName)!.add(callbacks.onError);
			unsubscribers.push(() => {
				const subscribers = this.fieldErrorSubscribers.get(fieldName);
				if (subscribers) {
					subscribers.delete(callbacks.onError!);
					if (subscribers.size === 0) {
						this.fieldErrorSubscribers.delete(fieldName);
					}
				}
			});
		}
		return () => unsubscribers.forEach((unsub) => unsub());
	}

	// Al setear un campo se ejecuta la función de notificar, que obtiene la función que hay suscrita al campo y la ejecuta para actualizar el estado local del form
	private notifyField(fieldName: string, value: any): void {
		const subscribers = this.fieldValueSubscribers.get(fieldName);
		if (subscribers) {
			subscribers.forEach((fn) => fn(value));
		}
	}

	//////////////////////
	//Gestion de errores//
	//////////////////////
	// Si el campo tiene un error se guarda hasta que el usuario lo solucione
	setFieldError(fieldName: string, hasError: boolean): void {
		const hadError = this.fieldErrors.has(fieldName);
		if (hasError) this.fieldErrors.add(fieldName);
		else this.fieldErrors.delete(fieldName);

		// Si el error que había antes es distinto, ejecutamos las funciones suscritas
		if (hadError !== hasError) {
			this.validationSubscribers.forEach((fn) => fn());
			const errorSubscribers = this.fieldErrorSubscribers.get(fieldName);
			if (errorSubscribers) {
				errorSubscribers.forEach((fn) => fn(hasError));
			}
		}
	}

	// Seteamos qué campos son requeridos
	setFieldRequired(fieldName: string, isRequired: boolean): void {
		if (isRequired) this.requiredFields.add(fieldName);
		else this.requiredFields.delete(fieldName);
	}

	// Registrar campo con validación de sugerencia
	setFieldSuggestionValidation(fieldName: string, suggestionFieldName: string): void {
		this.suggestionFields.set(fieldName, suggestionFieldName);
	}

	// Comprueba si un campo tiene validación de sugerencia registrada
	hasFieldSuggestionValidation(fieldName: string): boolean {
		return this.suggestionFields.has(fieldName);
	}

	clearFieldSuggestionValidation(fieldName: string): void {
		this.suggestionFields.delete(fieldName);
	}

	reset(): void {
		this.formSubmitStatusValue = FORM_SUBMIT_STATUS.PENDING;
		this.fieldValues = {};
		this.fieldErrors.clear();
		this.requiredFields.clear();
		this.suggestionFields.clear();
	}

	// Limpiamos el campo, quitamos el error y quitamos el requerido
	clearField(fieldName: string): void {
		delete this.fieldValues[fieldName];
		this.setFieldError(fieldName, false);
		this.requiredFields.delete(fieldName);
	}

	// Función para comprobar si un campo específico es válido
	isFieldValid(fieldName: string): boolean {
		return !this.fieldErrors.has(fieldName);
	}

	// Función para comprobar que los campos sean válidos para enviar
	isFormValid(): boolean {
		let isValid = true;
		for (const field of this.requiredFields) {
			const value = this.fieldValues[field];
			if (!value || value === '') {
				this.setFieldError(field, true);
				isValid = false;
				console.error(`${field} is required but has no value.`);
			}
			const suggestionFieldName = this.suggestionFields.get(field);
			if (suggestionFieldName) {
				const suggestionSelected = this.fieldValues[suggestionFieldName];
				if (!suggestionSelected) {
					this.setFieldError(field, true);
					isValid = false;
					console.error(`${field} has a suggestion field ${suggestionFieldName} but no value.`);
				}
			}
		}
		return isValid && this.fieldErrors.size === 0;
	}
}

export const formService = new FormService();
